import React, { useId, useState } from 'react';
import { motion } from 'framer-motion';
import { Calculator, TrendingUp, AlertTriangle, ArrowRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { scrollToSection } from '../../utils/scroll';
import { SITE_CONFIG } from '../../config';

const ROICalculator: React.FC = () => {
  const { t } = useTranslation();
  const messagesId = useId();
  const missedId = useId();
  const valueId = useId();
  const [messagesPerDay, setMessagesPerDay] = useState(50);
  const [missedPercentage, setMissedPercentage] = useState(30);
  const [avgPatientValue, setAvgPatientValue] = useState(80);

  const pct = (val: number, min: number, max: number) => ((val - min) / (max - min)) * 100;
  const trackFill = (val: number, min: number, max: number, color: string) =>
    `linear-gradient(to right, ${color} ${pct(val, min, max)}%, var(--roi-track) ${pct(val, min, max)}%)`;

  // Math logic — rangos de los sliders acotados a valores creíbles para una clínica dental
  // (antes permitían combinaciones como 300 msj/día x 80% x $500 = $2.64M/mes, poco creíble)
  const missedMessagesPerDay = Math.round(messagesPerDay * (missedPercentage / 100));
  const missedMessagesPerMonth = missedMessagesPerDay * 22; // approx 22 días laborables
  const lostRevenuePerMonth = missedMessagesPerMonth * avgPatientValue;
  const lostRevenuePerYear = lostRevenuePerMonth * 12;
  const roiMultiplier = lostRevenuePerMonth > 0 ? Math.round(lostRevenuePerMonth / SITE_CONFIG.pricing.proMonthly) : 0;

  return (
    <section style={{ padding: 'clamp(4rem, 8vh, 6rem) 0', position: 'relative' }}>
      <div className="container" style={{ maxWidth: 1240 }}>
        
        <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 8, color: 'var(--accent-teal)', marginBottom: '1rem', fontWeight: 600, fontSize: '0.9rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}
          >
            <Calculator size={18} />
            {t('roi.eyebrow', 'Calculadora de ROI')}
          </motion.div>
          <motion.h2
            initial={{ opacity: 0, y: 15 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            style={{ fontSize: 'clamp(2rem, 4vw, 3rem)', fontWeight: 300, color: 'var(--text-primary)', margin: 0, lineHeight: 1.1 }}
          >
            {t('roi.title1', '¿Cuánto dinero')} <span className="grad-text" style={{ fontWeight: 800 }}>{t('roi.title2', 'pierdes al mes?')}</span>
          </motion.h2>
          <p style={{ color: 'var(--text-secondary)', marginTop: '1rem', fontSize: '1.1rem' }}>
            {t('roi.subtitle', 'Cada mensaje sin responder es un paciente perdido. Descubre tu verdadero costo de oportunidad.')}
          </p>
        </div>

        <style>{`
          .roi-grid { display: grid; grid-template-columns: 0.85fr 1.15fr; gap: 2rem; align-items: stretch; }
          @media (max-width: 760px) { .roi-grid { grid-template-columns: 1fr; } }

          .roi-card-left { background: #161616; }
          [data-theme='light'] .roi-card-left { background: var(--bg-elevated); border: 1px solid var(--border-light); }

          .roi-card-right { background: #1a1414; }
          [data-theme='light'] .roi-card-right { background: #fdf2f2; border: 1px solid rgba(255,45,45,0.15); }

          .roi-highlight { background: rgba(255,255,255,0.05); }
          [data-theme='light'] .roi-highlight { background: rgba(0,0,0,0.035); }
        `}</style>
        <div className="roi-grid">

          {/* Controles (Sliders) */}
          <motion.div
            className="roi-card-left"
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            style={{
              borderRadius: 24, padding: '1.8rem',
              display: 'flex', flexDirection: 'column', gap: '1.8rem'
            }}
          >
            <style>{`
              :root { --roi-track: rgba(255,255,255,0.1); }
              [data-theme='light'] { --roi-track: rgba(0,0,0,0.1); }
              .roi-range { -webkit-appearance: none; appearance: none; width: 100%; height: 9px; border-radius: 999px; outline: none; }
              .roi-range::-webkit-slider-thumb { -webkit-appearance: none; width: 13px; height: 13px; border-radius: 50%; background: #fff; box-shadow: 0 2px 6px -1px rgba(0,0,0,0.5); cursor: pointer; margin-top: 0; }
              .roi-range::-moz-range-thumb { width: 13px; height: 13px; border-radius: 50%; background: #fff; border: none; box-shadow: 0 2px 6px -1px rgba(0,0,0,0.5); cursor: pointer; }
              [data-theme='light'] .roi-range::-webkit-slider-thumb { border: 2px solid rgba(0,0,0,0.15); }
              [data-theme='light'] .roi-range::-moz-range-thumb { border: 2px solid rgba(0,0,0,0.15); }
            `}</style>

            <div>
              <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: '0.7rem' }}>
                <label htmlFor={messagesId} style={{ color: 'var(--text-primary)', fontSize: '1rem', fontWeight: 700 }}>{t('roi.label_messages', 'Mensajes nuevos de WhatsApp al día')}</label>
                <span style={{ color: 'var(--accent-teal)', fontWeight: 400, fontSize: '1.1rem', lineHeight: 1 }}>{messagesPerDay}</span>
              </div>
              <input
                id={messagesId}
                className="roi-range"
                type="range" min="5" max="100" step="5"
                value={messagesPerDay} onChange={(e) => setMessagesPerDay(Number(e.target.value))}
                aria-valuetext={`${messagesPerDay} mensajes al día`}
                style={{ color: 'var(--accent-teal)', background: trackFill(messagesPerDay, 5, 100, 'var(--accent-teal)') }}
              />
            </div>

            <div>
              <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: '0.7rem' }}>
                <label htmlFor={missedId} style={{ color: 'var(--text-primary)', fontSize: '1rem', fontWeight: 700 }}>{t('roi.label_missed', '% de mensajes perdidos / respondidos tarde')}</label>
                <span style={{ color: 'var(--accent-purple)', fontWeight: 400, fontSize: '1.1rem', lineHeight: 1 }}>{missedPercentage}%</span>
              </div>
              <input
                id={missedId}
                className="roi-range"
                type="range" min="5" max="50" step="5"
                value={missedPercentage} onChange={(e) => setMissedPercentage(Number(e.target.value))}
                aria-valuetext={`${missedPercentage} por ciento`}
                style={{ color: 'var(--accent-purple)', background: trackFill(missedPercentage, 5, 50, 'var(--accent-purple)') }}
              />
              <p style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginTop: '0.6rem', margin: '0.6rem 0 0' }}>
                {t('roi.missed_hint', 'Estimado: clínicas sin automatización suelen perder cerca del 30%')}
              </p>
            </div>

            <div>
              <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: '0.7rem' }}>
                <label htmlFor={valueId} style={{ color: 'var(--text-primary)', fontSize: '1rem', fontWeight: 700 }}>{t('roi.label_value', 'Valor promedio de un paciente (USD)')}</label>
                <span style={{ color: 'var(--text-primary)', fontWeight: 400, fontSize: '1.1rem', lineHeight: 1 }}>${avgPatientValue}</span>
              </div>
              <input
                id={valueId}
                className="roi-range"
                type="range" min="20" max="200" step="10"
                value={avgPatientValue} onChange={(e) => setAvgPatientValue(Number(e.target.value))}
                aria-valuetext={`${avgPatientValue} dólares`}
                style={{ color: 'var(--text-primary)', background: trackFill(avgPatientValue, 20, 200, 'var(--text-primary)') }}
              />
            </div>
          </motion.div>

          {/* Resultados */}
          <motion.div
            className="roi-card-right"
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            style={{
              borderRadius: 24, padding: '1.8rem',
              display: 'flex', flexDirection: 'column', justifyContent: 'center',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, color: '#ff2d2d', marginBottom: '0.8rem' }}>
              <AlertTriangle size={24} />
              <h3 style={{ margin: 0, fontSize: '1.2rem', fontWeight: 600 }}>{t('roi.lost_title', 'Pérdida Estimada')}</h3>
            </div>

            <div style={{ marginBottom: '1rem' }}>
              <div style={{ fontSize: '0.9rem', color: 'var(--text-secondary)', marginBottom: '0.3rem' }}>{t('roi.lost_month', 'Pierdes cada mes aprox:')}</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.7rem', flexWrap: 'wrap' }}>
                <div style={{ fontSize: 'clamp(2.6rem, 4.5vw, 3.4rem)', fontWeight: 800, color: '#ff2d2d', lineHeight: 1 }}>
                  ${lostRevenuePerMonth.toLocaleString('en-US')}
                </div>
                <ArrowRight size={22} color="var(--text-muted)" style={{ flexShrink: 0 }} />
                <div style={{ fontSize: '1.2rem', fontWeight: 700, color: 'var(--text-primary)' }}>
                  {t('roi.lost_year', '¡Son')} ${lostRevenuePerYear.toLocaleString('en-US')} {t('roi.lost_year_end', 'al año!')}
                </div>
              </div>
            </div>

            <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', margin: '0 0 1rem', lineHeight: 1.4 }}>
              {t('roi.disclaimer', 'Estimación aproximada, no una garantía: asume que cada mensaje sin responder a tiempo equivale a una oportunidad de venta perdida.')}
            </p>

            <div className="roi-highlight" style={{ padding: '1.1rem 1.3rem', borderRadius: 16, marginBottom: '1.3rem' }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                <TrendingUp size={24} color="var(--accent-teal)" style={{ flexShrink: 0 }} />
                <div>
                  <p style={{ margin: '0 0 0.5rem', color: 'var(--text-primary)', fontWeight: 600, fontSize: '1rem' }}>
                    {t('roi.solution_title', 'RurayHUB recupera este dinero')}
                  </p>
                  <p style={{ margin: 0, color: 'var(--text-secondary)', fontSize: '0.9rem', lineHeight: 1.5 }}>
                    {roiMultiplier >= 2
                      ? t('roi.solution_desc_roi', 'Con lo que pierdes hoy, RurayHUB se paga solo {{multiplier}}x — el agente IA atiende al instante 24/7 y agenda esas citas automáticamente.', { multiplier: roiMultiplier })
                      : t('roi.solution_desc_low', 'El agente IA atiende al instante 24/7 y agenda esas citas automáticamente — así dejas de perder esas ventas.')}
                  </p>
                </div>
              </div>
            </div>

            <button
              onClick={() => scrollToSection('#cta-final')}
              className="trial-btn"
              style={{
                width: '100%', padding: '1rem', borderRadius: 12, border: 'none',
                fontFamily: 'var(--font-body)', fontWeight: 700, fontSize: '1.05rem',
                cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
              }}
            >
              {t('roi.btn_stop_losing', 'Dejar de perder dinero')}
              <ArrowRight size={18} />
            </button>
          </motion.div>

        </div>
      </div>
    </section>
  );
};

export default ROICalculator;
